
import React from 'react';
import { Sparkles, Compass, Route, LayoutGrid, Moon, Sun, Minimize, Maximize, Flame, BookText } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useKai } from '../contexts/KaiPresenceContext';
import { UserAvatar } from './UserAvatar';
import { KaiLogo } from './KaiLogo';
import { isClientLogicSecure } from '../services/geminiService';

type AppSection = 'chat' | 'features' | 'journey' | 'profile' | 'insights' | 'kai-settings' | 'library' | 'venn';

interface Props {
    activeSection: AppSection;
    setActiveSection: (section: AppSection) => void;
    user: any;
    logout: () => Promise<void>;
    isFullscreen: boolean;
    toggleFullscreen: () => void;
    userStats?: { streak: number, xp: number, level: number };
}

interface NavButtonProps {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    colorName: string; // Pass the active color name for dynamic glowing
}

const NavButton: React.FC<NavButtonProps> = ({ active, onClick, icon, label, colorName }) => {
    const { theme } = useTheme();

    // Dynamic Shadow/Glow based on personality color
    const glowMap: Record<string, string> = {
        indigo: 'shadow-indigo-500/40',
        rose: 'shadow-rose-500/40',
        emerald: 'shadow-emerald-500/40',
        amber: 'shadow-amber-500/40'
    };
    const activeGlow = glowMap[colorName] || glowMap.indigo;

    // Active State: Glassy background + Colored Text + Neon Shadow
    const activeClass = theme.mode === 'dark'
        ? `bg-slate-800 text-${colorName}-400 ${activeGlow} shadow-lg border border-${colorName}-500/20`
        : `bg-white text-${colorName}-600 shadow-lg shadow-slate-200 border border-${colorName}-100`;

    // Inactive State: Subtle ghost hover
    const inactiveClass = `text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-600 dark:hover:text-slate-200 border border-transparent`;

    return (
        <button
            onClick={onClick}
            className={`p-3.5 rounded-2xl transition-all duration-300 group relative flex items-center justify-center ${active ? activeClass : inactiveClass
                } ${active ? 'scale-105' : 'hover:scale-105'}`}
            title={label}
        >
            {/* Icon Container */}
            <div className="relative z-10">
                {icon}
            </div>

            {/* Hover Tooltip (Detached) */}
            <span className={`absolute left-16 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1 pointer-events-none whitespace-nowrap z-50 shadow-xl border ${theme.mode === 'dark' ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-800'}`}>
                {label}
            </span>
        </button>
    );
};

export const Sidebar: React.FC<Props> = ({
    activeSection,
    setActiveSection,
    user,
    isFullscreen,
    toggleFullscreen,
    userStats
}) => {
    const { theme, toggleTheme, getKaiTheme } = useTheme();
    const { state: kaiState } = useKai();

    const kaiTheme = getKaiTheme(kaiState.color);
    const svgStrokeColor = `text-${kaiTheme.name}-500`;

    // Map color names to hex for avatar
    const getHexColor = (c: string) => {
        const map: Record<string, string> = {
            indigo: '6366f1',
            rose: 'f43f5e',
            emerald: '10b981',
            amber: 'f59e0b'
        };
        return map[c] || '6366f1';
    };

    // XP Ring Logic
    const xpProgress = userStats ? (userStats.xp % 500) / 500 : 0.25;
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (xpProgress * circumference);

    return (
        <nav className={`hidden md:flex flex-col w-[88px] h-[96vh] my-[2vh] ml-4 py-8 items-center justify-between z-40 transition-all duration-500 ease-spring ${theme.components.sidebar.container}`}>

            {/* Top: Brand & Nav */}
            <div className="flex flex-col items-center gap-6 w-full px-4">
                {/* Brand Logo */}
                <div className="mb-4 hover:scale-110 hover:rotate-3 transition-transform duration-500 cursor-pointer relative group" onClick={() => setActiveSection('kai-settings')} title="System Settings">
                    <KaiLogo size={48} variant="symbol" color={kaiState.color} className="drop-shadow-lg" />
                    {/* Settings Hint */}
                    <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-900 opacity-0 group-hover:opacity-100 transition-opacity bg-${kaiTheme.name}-500`}></div>
                </div>

                {/* Navigation Stack */}
                <div className="flex flex-col gap-4 w-full">
                    <NavButton
                        active={activeSection === 'chat'}
                        onClick={() => setActiveSection('chat')}
                        icon={<Sparkles size={22} strokeWidth={2} />}
                        label="Communicate"
                        colorName={kaiTheme.name}
                    />
                    <NavButton
                        active={activeSection === 'insights'}
                        onClick={() => setActiveSection('insights')}
                        icon={<Compass size={22} strokeWidth={2} />}
                        label="Blueprints"
                        colorName={kaiTheme.name}
                    />
                    <NavButton
                        active={activeSection === 'journey'}
                        onClick={() => setActiveSection('journey')}
                        icon={<Route size={22} strokeWidth={2} />}
                        label="Timeline"
                        colorName={kaiTheme.name}
                    />
                    <NavButton
                        active={activeSection === 'features'}
                        onClick={() => setActiveSection('features')}
                        icon={<LayoutGrid size={22} strokeWidth={2} />}
                        label="Neural Tools"
                        colorName={kaiTheme.name}
                    />
                </div>
            </div>

            {/* Bottom: Utilities & User */}
            <div className="flex flex-col items-center gap-4 w-full px-4">

                {/* Streak Pill */}
                {userStats && (
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full border ${theme.mode === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`} title="Current Streak">
                        <Flame size={12} className={userStats.streak > 1 ? 'fill-orange-500 text-orange-500' : 'text-slate-400'} />
                        <span className={`text-[10px] font-bold ${userStats.streak > 1 ? 'text-orange-500' : 'text-slate-400'}`}>
                            {userStats.streak}
                        </span>
                    </div>
                )}

                {/* Utilities Block */}
                <div className={`flex flex-col gap-1 p-1.5 rounded-2xl border ${theme.mode === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-100/50 border-slate-200'}`}>
                    
                    {/* Library - Dev Only */}
                    {!isClientLogicSecure() && (
                        <button
                            onClick={() => setActiveSection('library')}
                            className={`p-2 rounded-xl transition-all ${activeSection === 'library' ? (theme.mode === 'dark' ? 'text-white bg-slate-700' : 'text-slate-900 bg-white shadow-sm') : `text-slate-400 hover:text-${kaiTheme.name}-500`}`}
                            title="Library (Dev)"
                        >
                            <BookText size={18} />
                        </button>
                    )}

                    <button
                        onClick={toggleTheme}
                        className={`p-2 rounded-xl transition-all ${theme.mode === 'dark' ? 'text-amber-400 hover:bg-slate-700' : `text-slate-400 hover:text-amber-500`}`}
                        title="Toggle Theme"
                    >
                        {theme.mode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                    </button>

                    <button
                        onClick={toggleFullscreen}
                        className="p-2 rounded-xl transition-all text-slate-400 hover:text-blue-500"
                        title="Fullscreen"
                    >
                        {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                    </button>

                    {/* Security Status Indicator */}
                    <div
                        className={`w-2 h-2 rounded-full mx-auto my-1 ${isClientLogicSecure() ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'}`}
                        title={isClientLogicSecure() ? "Security Status: Secure (Cloud Only)" : "Security Status: Dev (Local Logic)"}
                    />
                </div>

                {/* Profile Core */}
                <button
                    onClick={() => setActiveSection('profile')}
                    className={`relative group w-12 h-12 flex items-center justify-center transition-transform duration-300 hover:scale-105 ${activeSection === 'profile' ? 'scale-105' : ''}`}
                    title="Identity Core"
                >
                    {/* XP Progress Ring */}
                    <div className="absolute inset-[-6px]">
                        <svg className="w-full h-full rotate-[-90deg]" viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r={radius} fill="none" stroke={theme.mode === 'dark' ? '#334155' : '#e2e8f0'} strokeWidth="2" className="opacity-50" />
                            <circle
                                cx="25"
                                cy="25"
                                r={radius}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeDasharray={circumference}
                                strokeDashoffset={strokeDashoffset}
                                strokeLinecap="round"
                                className={`${svgStrokeColor} transition-all duration-1000 ease-out`}
                            />
                        </svg>
                    </div>

                    {/* Avatar */}
                    <div className={`w-full h-full rounded-full overflow-hidden border-2 transition-colors relative z-10 ${activeSection === 'profile'
                        ? `border-${kaiTheme.name}-500 shadow-md`
                        : 'border-white dark:border-slate-700'
                        }`}>
                        <div className={kaiTheme.bgLight + " w-full h-full"}>
                            <UserAvatar
                                user={user}
                                className="w-full h-full object-cover"
                                backgroundColor={getHexColor(kaiState.color)}
                            />
                        </div>
                    </div>

                    {/* Level Badge */}
                    {userStats && (
                        <div className={`absolute -bottom-2 z-20 px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-tight shadow-sm border transition-colors ${activeSection === 'profile'
                            ? `bg-${kaiTheme.name}-500 text-white border-${kaiTheme.name}-600`
                            : 'bg-slate-800 text-slate-300 border-slate-700'
                            }`}>
                            LVL {userStats.level}
                        </div>
                    )}
                </button>
            </div>
        </nav>
    );
};
